import React from "react";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import LockIcon from "@mui/icons-material/Lock";
import {
  Box,
  Card,
  CardContent,
  Container,
  FormControlLabel,
  Checkbox,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import { LoadingButton } from "@mui/lab";
import BrandingLogo from "components/branding-logo";
import { useSite } from "hooks/use-site";
import { useAppDispatch } from "store";
import { updateUserAccount } from "../../../service/api";
import { errorNotification } from "../../../features/notifications/notificationsSlice";
import { useNavigate } from "react-router-dom";
import { useAccountQuery } from "../../../features/auth/authApi";

type DeclarationFormValues = {
  date: string;
  fullName: string;
  agree: boolean;
  acceptTnC: boolean;
};

const validationSchema = Yup.object({
  date: Yup.string().required("Date is required"),
  fullName: Yup.string().required("Full Name is required"),
  agree: Yup.boolean().oneOf([true], "You must agree to the declaration"),
  acceptTnC: Yup.boolean().oneOf(
    [true],
    "You must accept our Terms and Conditions"
  ),
});

const DeclarationForm: React.FC = () => {
  const { termsAndConditionsUrl } = useSite();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: account } = useAccountQuery();

  const today = new Date().toISOString().slice(0, 10);

  const initialValues: DeclarationFormValues = {
    date: today,
    fullName: "",
    agree: false,
    acceptTnC: false,
  };

  const onSubmit = async (
    values: DeclarationFormValues,
    { setSubmitting }: FormikHelpers<DeclarationFormValues>
  ) => {
    setSubmitting(true);

    const form3 = localStorage.getItem("form3_json");

    try {
      await updateUserAccount({ form3_json: form3 });

      localStorage.removeItem("form3_json");
    } catch (err: any) {
      dispatch(errorNotification(err.message || "Update failed"));
    } finally {
      if (account?.user.form3_json) {
        navigate("/profile");
        window.location.reload();
      } else window.location.reload();

      setSubmitting(false);
    }
  };

  const {
    handleSubmit,
    getFieldMeta,
    handleChange,
    handleBlur,
    isSubmitting,
    setFieldValue,
  } = useFormik<DeclarationFormValues>({
    initialValues,
    validationSchema,
    onSubmit,
  });

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ mt: 4 }}
    >
      <Container maxWidth="md">
        <form onSubmit={handleSubmit}>
          <Card
            elevation={24}
            component={motion.div}
            layoutId="declaration-card"
          >
            <Box paddingY={2.5} paddingX={4} sx={{ bgcolor: "brand.auth" }}>
              <BrandingLogo width={200} />
            </Box>
            <CardContent>
              <Stack spacing={4}>
                <Box display="flex" alignItems="center">
                  <LockIcon fontSize="large" />
                  <Typography variant="h6" fontWeight="600" ml={1}>
                    DECLARATION
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body1" paragraph>
                    By signing this questionnaire, I confirm that:
                  </Typography>
                  <Typography variant="body2" paragraph>
                    (i) The information provided in this questionnaire is
                    accurate, complete, and up-to-date. I undertake to promptly
                    notify the Company in writing of any changes to the
                    submitted information;
                  </Typography>
                  <Typography variant="body2" paragraph>
                    (ii) I agree to use the services provided by the Company
                    exclusively for legitimate purposes;
                  </Typography>
                  <Typography variant="body2" paragraph>
                    (iii) I acknowledge that the Company reserves the right to
                    request additional information or documents related to the
                    submitted information, and I commit to providing them within
                    the time frame specified by the Company;
                  </Typography>
                  <Typography variant="body2" paragraph>
                    (iv) I have read, understood, and accepted the terms of the
                    Company’s Privacy Policy, available at emoni.io, including
                    the purposes and legal basis of personal data processing,
                    the involvement of data processors and recipients, the
                    exercise of data subject rights, and the retention period of
                    personal data;
                  </Typography>
                  <Typography variant="body2" paragraph>
                    (v) I understand that the controller of the personal data
                    processed under this questionnaire is iLedgends s.r.o.;
                  </Typography>
                  <Typography variant="body2" paragraph>
                    (vi) I confirm that the personal data provided in this
                    questionnaire has been lawfully obtained, and I have the
                    right to disclose such data to the Company for the purpose
                    of applying for the requested service(s);
                  </Typography>
                  <Typography variant="body2" paragraph>
                    (vii) I/we declare that I am/we are currently not, and have
                    never been, involved in any bankruptcy proceedings,
                    unsatisfied judgments, or criminal sanctions.
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      type="date"
                      disabled={true}
                      label="Date"
                      name="date"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      value={getFieldMeta("date").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("date").touched &&
                        Boolean(getFieldMeta("date").error)
                      }
                      helperText={
                        getFieldMeta("date").touched &&
                        getFieldMeta("date").error
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Full Name"
                      name="fullName"
                      variant="outlined"
                      fullWidth
                      value={getFieldMeta("fullName").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("fullName").touched &&
                        Boolean(getFieldMeta("fullName").error)
                      }
                      helperText={
                        getFieldMeta("fullName").touched &&
                        getFieldMeta("fullName").error
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="agree"
                          checked={getFieldMeta("agree").value}
                          onChange={(e) =>
                            setFieldValue("agree", e.target.checked)
                          }
                          onBlur={handleBlur}
                        />
                      }
                      label="I agree"
                    />
                    {getFieldMeta("agree").touched &&
                      getFieldMeta("agree").error && (
                        <Typography variant="caption" color="error">
                          {getFieldMeta("agree").error}
                        </Typography>
                      )}

                    <FormControlLabel
                      control={
                        <Checkbox
                          name="acceptTnC"
                          checked={getFieldMeta("acceptTnC").value}
                          onChange={(e) =>
                            setFieldValue("acceptTnC", e.target.checked)
                          }
                          onBlur={handleBlur}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          I accept{" "}
                          <a
                            href={termsAndConditionsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Terms and Conditions
                          </a>
                        </Typography>
                      }
                    />
                    {getFieldMeta("acceptTnC").touched &&
                      getFieldMeta("acceptTnC").error && (
                        <Typography variant="caption" color="error">
                          {getFieldMeta("acceptTnC").error}
                        </Typography>
                      )}
                  </Grid>
                </Grid>

                <Stack spacing={2}>
                  <LoadingButton
                    fullWidth
                    loading={isSubmitting}
                    variant="contained"
                    type="submit"
                  >
                    Submit
                  </LoadingButton>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </form>
      </Container>
    </Box>
  );
};

export default DeclarationForm;
